import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const NAV = [
  { path: '/app/dashboard', label: 'Dashboard', icon: '⊞' },
  { path: '/app/vitals', label: 'Daily Vitals', icon: '♥' },
  { path: '/app/medications', label: 'Medications', icon: '💊' },
  { path: '/app/profile', label: 'Health Profile', icon: '☰' },
  { path: '/app/community', label: 'AI Doctors', icon: '⚕' },
  { path: '/app/connect', label: 'Connect / Journal', icon: '⌚' },
  { path: '/app/import', label: 'Import Data', icon: '↑' },
  { path: '/app/report', label: 'Health Report', icon: '⬇' },
];

export default function Layout({ children }: { children: React.ReactNode }) {
  const { currentUser, userProfile, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <aside
        className={`${sidebarOpen ? 'w-56' : 'w-16'} flex-shrink-0 bg-white border-r border-gray-200 flex flex-col transition-all duration-200`}
      >
        {/* Logo */}
        <div className="flex items-center gap-2 px-4 py-5 border-b border-gray-100">
          <span className="text-2xl text-emerald-600">♥</span>
          {sidebarOpen && <span className="font-bold text-gray-900 text-lg">VitalQuant</span>}
        </div>

        {/* Nav links */}
        <nav className="flex-1 py-4 space-y-1 px-2">
          {NAV.map(({ path, label, icon }) => (
            <NavLink
              key={path}
              to={path}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-emerald-50 text-emerald-700'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }`
              }
            >
              <span className="text-base w-5 text-center">{icon}</span>
              {sidebarOpen && <span>{label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* Quant Quadrivium */}
        <div className="border-t border-gray-100 px-3 pt-3 pb-1">
          {sidebarOpen && (
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-1 mb-1">🏛 Quant Quadrivium</p>
          )}
          {[
            { href: 'https://quantclaw.ai', icon: '💰', label: 'AILabQuant — Wealth' },
            { href: 'https://talkoru.com', icon: '🧠', label: 'Talkoru — Wisdom' },
            { href: 'https://lexquant.ai', icon: '⚖️', label: 'LexQuant — Justice' },
          ].map(({ href, icon, label }) => (
            <a key={href} href={href} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition-colors">
              <span className="w-5 text-center text-base">{icon}</span>
              {sidebarOpen && <span>{label} ↗</span>}
            </a>
          ))}
        </div>

        {/* User section */}
        <div className="border-t border-gray-100 p-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-semibold text-sm flex-shrink-0">
              {(userProfile?.display_name || currentUser?.email || 'V')[0].toUpperCase()}
            </div>
            {sidebarOpen && (
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-gray-800 truncate">
                  {userProfile?.display_name || 'User'}
                </p>
                <button
                  onClick={handleLogout}
                  className="text-xs text-gray-400 hover:text-red-500 transition-colors"
                >
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar */}
        <header className="bg-white border-b border-gray-200 px-6 py-3 flex items-center gap-4">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-gray-400 hover:text-gray-700 transition-colors"
          >
            ☰
          </button>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto p-6">{children}</main>
      </div>
    </div>
  );
}
